import { useState } from 'react';
import runtimeGuides from '../../catalog/runtime-setup.json';

type Os = 'windows' | 'mac';

interface RuntimeGuide {
  name: string;
  what: string;
  install: Record<Os, string>;
  signIn: string;
}

/** The runtimes a node can drive (catalog file, so product names stay out of src); mirrors apps/brunel-node/catalog/runtimes.json. */
const RUNTIMES: readonly RuntimeGuide[] = runtimeGuides;

/** Step 1 of adding a node: a computer needs at least one AI runtime before it can take work. */
export function RuntimeSetup() {
  const [os, setOs] = useState<Os>(() => (
    typeof navigator !== 'undefined' && navigator.userAgent.includes('Windows') ? 'windows' : 'mac'
  ));
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(text);
    window.setTimeout(() => setCopied(null), 2000);
  };
  return (
    <details className="card stack install settings-install">
      <summary><strong>Step 1 — install a runtime on the computer first</strong></summary>
      <small className="muted">
        A node only runs work through an AI runtime already installed on that computer. Install at least one below
        (about 5 minutes), sign in, then do step 2. If you add a runtime later, run the Add node install again so the
        node picks it up.
      </small>
      <div className="row segmented" role="tablist" aria-label="Operating system">
        <button type="button" role="tab" aria-selected={os === 'windows'} className={os === 'windows' ? 'active' : ''} onClick={() => setOs('windows')}>Windows</button>
        <button type="button" role="tab" aria-selected={os === 'mac'} className={os === 'mac' ? 'active' : ''} onClick={() => setOs('mac')}>Mac / Linux</button>
      </div>
      <small className="muted">
        {os === 'windows' ? 'Commands go in PowerShell (Start menu → type PowerShell).' : 'Commands go in Terminal (Spotlight → type Terminal).'}
      </small>
      <ol className="stack">
        {RUNTIMES.map((r) => {
          const text = r.install[os];
          const isCommand = !text.startsWith('Download');
          return (
            <li key={r.name} className="stack">
              <span><strong>{r.name}</strong> <span className="muted">— {r.what}</span></span>
              {isCommand ? (
                <div className="row">
                  <code className="block">{text}</code>
                  <button type="button" onClick={() => void copy(text)}>{copied === text ? 'Copied' : 'Copy'}</button>
                </div>
              ) : (
                <small>{text}</small>
              )}
              <small className="muted">{r.signIn}</small>
            </li>
          );
        })}
      </ol>
      <small className="muted">
        Not sure which? Pick the first one if you already pay for its plan. Step 2: give the computer a name below
        and press Add node.
      </small>
    </details>
  );
}
